import { GridRowModel, GridRowsProp } from "@mui/x-data-grid";
import { randomId } from "@mui/x-data-grid-generator";

import {
  getFirstGridRequiredErrorMessage,
  getGridRequiredError,
  isGridEmptyValue,
  parseGridRows,
} from "@/components/form/entities/grid/grid-value-validation";

import {
  DropdownStaticDatasource,
  DropdownDynamicDatasource,
  GridHeaderItem,
} from "./grid-data-grid.types";

export const isComputedHeader = (header: GridHeaderItem) =>
  header.type === "row-expression";

export const isUserEditableHeader = (header: GridHeaderItem) =>
  !header.readonly && !header.disabled && !isComputedHeader(header);

export const isEditModeHeader = (header: GridHeaderItem) =>
  isComputedHeader(header) || isUserEditableHeader(header);

export const getHeaderField = (header: GridHeaderItem) => header.keyValue;

export const parseRowsFromValue = (value: unknown): GridRowsProp => {
  const parsedRows = parseGridRows(value);
  return parsedRows as GridRowsProp;
};

export const canAddRow = (rowsCount: number, maxRows?: number) =>
  maxRows === undefined || rowsCount < maxRows;

export const isEmptyValue = isGridEmptyValue;

export const getDropdownStaticDatasource = (
  datasource: unknown,
): DropdownStaticDatasource | undefined => {
  if (
    !datasource ||
    typeof datasource !== "object" ||
    !("type" in datasource) ||
    (datasource as { type?: string }).type !== "static"
  ) {
    return undefined;
  }

  const staticDatasource = datasource as DropdownStaticDatasource;
  if (!Array.isArray(staticDatasource.options)) {
    return undefined;
  }

  return staticDatasource;
};

export const getDropdownOptions = (header: GridHeaderItem) => {
  const staticDatasource = getDropdownStaticDatasource(header.datasource);
  if (!staticDatasource) {
    return [];
  }

  return staticDatasource.options.map((opt) => ({
    ...opt,
    label:
      staticDatasource.displayOption === "label_value"
        ? `${opt.label} (${opt.value})`
        : opt.label,
  }));
};

export const getDropdownDynamicDatasource = (
  datasource: unknown,
): DropdownDynamicDatasource | undefined => {
  if (
    !datasource ||
    typeof datasource !== "object" ||
    !("type" in datasource) ||
    (datasource as { type?: string }).type !== "dynamic"
  ) {
    return undefined;
  }

  return datasource as DropdownDynamicDatasource;
};

export const getDefaultCellValue = (header: GridHeaderItem) => {
  if (isComputedHeader(header)) {
    return null;
  }

  if (header.type === "input") {
    if (typeof header.defaultValue === "string") {
      return header.defaultValue;
    }
    if (typeof header.defaultValue === "number") {
      return String(header.defaultValue);
    }
    return null;
  }

  if (header.type === "number") {
    if (typeof header.defaultValue === "number") {
      return header.defaultValue;
    }
    if (typeof header.defaultValue === "string" && header.defaultValue.trim()) {
      const parsed = Number(header.defaultValue);
      return Number.isNaN(parsed) ? null : parsed;
    }
    return null;
  }

  if (header.type === "date") {
    if (typeof header.defaultValue === "number") {
      return header.defaultValue;
    }
    if (typeof header.defaultValue === "string" && header.defaultValue.trim()) {
      const parsed = Number(header.defaultValue);
      return Number.isNaN(parsed) ? null : parsed;
    }
    return null;
  }

  if (header.type === "dropdown") {
    const staticDatasource = getDropdownStaticDatasource(header.datasource);
    if (
      staticDatasource?.defaultValue &&
      !staticDatasource.defaultValue.isReference &&
      typeof staticDatasource.defaultValue.value === "string"
    ) {
      return staticDatasource.defaultValue.value;
    }
    return null;
  }

  return null;
};

export const getRequiredError = (header: GridHeaderItem, value: unknown) => {
  if (header.disabled || header.readonly) {
    return undefined;
  }
  return getGridRequiredError(header, value);
};

export const getFirstGridErrorMessage = (
  rows: GridRowsProp,
  headers: GridHeaderItem[],
) =>
  getFirstGridRequiredErrorMessage(rows as Record<string, unknown>[], headers);

export const buildEmptyRow = (headers: GridHeaderItem[]) => {
  const row: Record<string, unknown> = {
    id: randomId(),
    isNew: true,
  };

  headers.forEach((header) => {
    row[getHeaderField(header)] = getDefaultCellValue(header);
  });

  return row as GridRowModel;
};
